import {
  ArrowLeftOutlined,
  ClockCircleOutlined,
  RightOutlined
} from "@ant-design/icons"
import { Flex, Typography, theme } from "antd"
import React, { useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Link, Navigate, useParams } from "react-router-dom"

import { CardComponent } from "../../components/Card/CardComponent.tsx"
import { generateDocumentTitle } from "../../constants/document-title.ts"
import { RoutesPaths } from "../../constants/routes.ts"
import { useAppSelector } from "../../hooks/use-app-selector.ts"
import { getGuideModule } from "./content/modules.ts"
import { isGuideModuleAccessible } from "./content/guide-permissions.ts"
import {
  getAccessibleLessonsForArea,
  getAreaProgress,
  getLearningArea,
  isLearningAreaAccessible
} from "./content/learning-areas.ts"
import { PRODUCT_STORY_ID } from "./content/product-story.ts"
import { useGuideProgress } from "./hooks/use-guide-progress.ts"
import type { GuideModuleSlug } from "./types/guide-content.types.ts"

const { Title, Text } = Typography

/** What this page actually needs to render — real `GuideLearningArea`s and
 * a bare `GuideModule` (the "start-here" deep-link fallback) both satisfy
 * this structurally, with no need to force a module slug into the area
 * id's narrower union. */
interface AcademyHubTarget {
  titleKey: string
  descriptionKey: string
  moduleSlugs: GuideModuleSlug[]
}

/**
 * The "Learning Hub" for one area — Sales, Projects, etc. Clicking an area
 * card on the Learn EZBuild landing page lands here, never straight into
 * lesson #1: a title, a real (never fabricated) lesson count/progress, and
 * a Recommended Path the user can follow in order or jump into anywhere.
 *
 * Also resolves a bare `GuideModule` slug directly (not just an area id) —
 * "start-here" isn't one of the six browsable areas, and this keeps the
 * pre-existing `/guide/academy/:slug` deep links (Quick Start, `GuideHelp
 * Trigger`, any bookmark) working unchanged as a single-module area of one.
 */
export const GuideAcademyModulePage = () => {
  const { t } = useTranslation()
  const { moduleSlug: idParam } = useParams<{ moduleSlug: string }>()
  const { isLessonComplete, progress } = useGuideProgress()
  const { token } = theme.useToken()
  const pages = useAppSelector((state) => state.auth?.permissions?.pages)
  const codes = useAppSelector((state) => state.auth?.permissions?.codes)

  const areaMatch = getLearningArea(idParam ?? "")
  const area =
    areaMatch && isLearningAreaAccessible(areaMatch, pages) ? areaMatch : null
  const fallbackModuleMatch = areaMatch ? null : getGuideModule(idParam ?? "")
  const fallbackModule =
    fallbackModuleMatch &&
    isGuideModuleAccessible(fallbackModuleMatch.slug, pages)
      ? fallbackModuleMatch
      : null
  // An id that resolves to a real area/module the user has no permission
  // for is treated exactly like an id that resolves to nothing — the Guide
  // never confirms a feature exists by rendering even its empty state for
  // someone who can't access it.
  const resolvedArea: AcademyHubTarget | null = useMemo(
    () =>
      area ??
      (fallbackModule
        ? {
            titleKey: fallbackModule.titleKey,
            descriptionKey: fallbackModule.descriptionKey,
            moduleSlugs: [fallbackModule.slug]
          }
        : null),
    [area, fallbackModule]
  )

  useEffect(() => {
    if (resolvedArea) generateDocumentTitle(t(resolvedArea.titleKey))
  }, [resolvedArea, t])

  const lessons = useMemo(
    () =>
      resolvedArea
        ? getAccessibleLessonsForArea(resolvedArea, pages, codes)
        : [],
    [resolvedArea, pages, codes]
  )
  const areaProgress = getAreaProgress(lessons, progress.completedLessonIds)

  const isStartHere = resolvedArea?.moduleSlugs[0] === "start-here"
  // start-here always has the Product Story card even with zero lessons, so
  // it's never truly empty — every other target with no accessible lessons
  // is treated exactly like one that doesn't exist at all: no "coming soon"
  // destination is ever rendered, matching Browse by Area's own rule that a
  // card only exists once real content backs it (see `learning-areas.ts`).
  if (!resolvedArea || (!isStartHere && lessons.length === 0))
    return (
      <Navigate to={`${RoutesPaths.guide.academy}?unavailable=1`} replace />
    )

  return (
    <Flex vertical gap="middle" className="mx-auto max-w-3xl pb-8">
      <Link
        to={RoutesPaths.guide.academy}
        className="flex items-center gap-1 text-sm text-gray-500"
      >
        <ArrowLeftOutlined /> {t("guide.academy.landing.title")}
      </Link>

      <Flex vertical gap={2}>
        <Title level={2} className="!mb-0">
          {t(resolvedArea.titleKey)}
        </Title>
        <Text type="secondary">{t(resolvedArea.descriptionKey)}</Text>
        {areaProgress.total > 0 && (
          <Text type="secondary" className="mt-1 text-sm">
            {t("guide.academy.areaProgress", {
              completed: areaProgress.completed,
              total: areaProgress.total
            })}
          </Text>
        )}
      </Flex>

      {isStartHere && (
        <Link to={RoutesPaths.guide.story}>
          <CardComponent
            hoverable
            className="cursor-pointer border-none"
            style={{ background: token.colorPrimaryBg }}
          >
            <Flex justify="space-between" align="center" gap={12}>
              <Flex vertical gap={2}>
                <Text strong>{t("guide.story.documentTitle")}</Text>
                <Text type="secondary" className="text-sm">
                  {t("guide.story.steps.bigPicture.subtitle")}
                </Text>
                {progress.completedLessonIds.includes(PRODUCT_STORY_ID) && (
                  <Text type="success" className="text-xs mt-1">
                    {t("guide.academy.completedBadge")}
                  </Text>
                )}
              </Flex>
              <RightOutlined style={{ color: token.colorPrimary }} />
            </Flex>
          </CardComponent>
        </Link>
      )}

      {lessons.length > 0 && (
        <Flex vertical gap={4}>
          <Text strong className="text-sm">
            {t("guide.academy.recommendedPath")}
          </Text>
          <Flex vertical gap={8}>
            {lessons.map((lesson, index) => {
              const complete = isLessonComplete(lesson.id)
              return (
                <Link key={lesson.id} to={RoutesPaths.guide.lesson(lesson.id)}>
                  <CardComponent hoverable className="cursor-pointer">
                    <Flex align="center" gap={12}>
                      <Flex
                        align="center"
                        justify="center"
                        className="size-7 shrink-0 rounded-full text-xs font-semibold"
                        style={{ background: token.colorFillTertiary }}
                      >
                        {index + 1}
                      </Flex>
                      <Flex vertical gap={2} className="min-w-0 flex-1">
                        <Text strong>{t(lesson.titleKey)}</Text>
                        <Text type="secondary" className="text-sm">
                          {t(lesson.descriptionKey)}
                        </Text>
                        <Flex align="center" gap={6} className="mt-1">
                          <ClockCircleOutlined className="text-xs text-gray-400" />
                          <Text type="secondary" className="text-xs">
                            {t("guide.lesson.duration", {
                              minutes: lesson.durationMinutes
                            })}
                          </Text>
                          {complete && (
                            <Text type="success" className="text-xs">
                              {t("guide.academy.completedBadge")}
                            </Text>
                          )}
                        </Flex>
                      </Flex>
                      <RightOutlined className="text-gray-300" />
                    </Flex>
                  </CardComponent>
                </Link>
              )
            })}
          </Flex>
        </Flex>
      )}
    </Flex>
  )
}
